import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection, addDoc, getDocs, doc, updateDoc,
  query, orderBy, Timestamp, increment,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { MANAGE_ROLES } from '../../utils/roles';
import useSeo from '../../hooks/useSeo';
import './Feedback.css';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════

const MOODS = [
  { emoji: '😠', label: 'Very Unhappy', type: 'complaint',  color: '#ef4444' },
  { emoji: '😕', label: 'Unhappy',      type: 'complaint',  color: '#f97316' },
  { emoji: '😐', label: 'Neutral',      type: 'feedback',   color: '#94a3b8' },
  { emoji: '🙂', label: 'Happy',        type: 'suggestion', color: '#f59e0b' },
  { emoji: '😍', label: 'Loved it!',    type: 'compliment', color: '#22c55e' },
];

const FEEDBACK_TYPES = [
  { value: 'compliment',  label: '🌟 Compliment',  color: '#22c55e' },
  { value: 'suggestion',  label: '💡 Suggestion',  color: '#f59e0b' },
  { value: 'complaint',   label: '⚠️ Complaint',   color: '#ef4444' },
  { value: 'feedback',    label: '💬 Feedback',    color: '#f59e0b' },
];

const CATEGORIES = [
  { value: 'food_quality', label: '🍛 Food Quality'   },
  { value: 'service',      label: '👥 Service'         },
  { value: 'cleanliness',  label: '✨ Cleanliness'     },
  { value: 'wait_time',    label: '⏱️ Wait Time'       },
  { value: 'ambiance',     label: '🏮 Ambiance'        },
  { value: 'value',        label: '💰 Value for Money' },
  { value: 'online_order', label: '📱 Online Ordering' },
  { value: 'catering',     label: '🎉 Catering'        },
  { value: 'other',        label: '💬 Other'           },
];

const DIMENSIONS = [
  { key: 'food',        label: '🍛 Food Quality' },
  { key: 'service',     label: '👥 Service'       },
  { key: 'cleanliness', label: '✨ Cleanliness'   },
  { key: 'speed',       label: '⚡ Speed'          },
  { key: 'value',       label: '💰 Value'          },
];

const STATUS_TABS = [
  { value: 'pending',      label: 'Inbox',      icon: '📬', color: '#f59e0b' },
  { value: 'acknowledged', label: 'In Review',  icon: '🔍', color: '#f59e0b' },
  { value: 'resolved',     label: 'Resolved',   icon: '✅', color: '#22c55e' },
];

const PRIORITY_TAGS = [
  { value: 'low',    label: 'Low',    color: '#64748b' },
  { value: 'medium', label: 'Medium', color: '#f59e0b' },
  { value: 'high',   label: 'High',   color: '#ef4444' },
  { value: 'urgent', label: 'Urgent', color: '#dc2626' },
];

const EMPTY_DIMENSIONS = { food: 0, service: 0, cleanliness: 0, speed: 0, value: 0 };

const EMPTY_FORM = {
  mood: null, type: 'feedback', category: 'food_quality', rating: 0,
  dimensionalRatings: { ...EMPTY_DIMENSIONS }, npsScore: null,
  subject: '', message: '', name: '', email: '', phone: '',
  anonymous: false, visitDate: '', orderNumber: '',
};

// ═══════════════════════════════════════════════════════════════
// SMALL UTILITY COMPONENTS
// ═══════════════════════════════════════════════════════════════

function TypeBadge({ type }) {
  const t = FEEDBACK_TYPES.find(f => f.value === type) || FEEDBACK_TYPES[3];
  return <span className="fb-type-badge" style={{ '--badge-color': t.color }}>{t.label}</span>;
}

function CategoryLabel({ category }) {
  const c = CATEGORIES.find(cat => cat.value === category);
  return <span className="fb-cat-label">{c ? c.label : category}</span>;
}

function PriorityBadge({ priority }) {
  const p = PRIORITY_TAGS.find(t => t.value === priority);
  if (!p) return null;
  return <span className="fb-priority-badge" style={{ '--p-color': p.color }}>{p.label}</span>;
}

function SlaTag({ item }) {
  if (item.status !== 'pending' || item.type === 'compliment') return null;
  const hoursOld = (Date.now() - (item.submittedAt?.toDate?.()?.getTime?.() || Date.now())) / 3600000;
  if (hoursOld > 24) return <span className="fb-sla-tag fb-sla-tag--overdue">🔴 Overdue</span>;
  if (hoursOld > 12) return <span className="fb-sla-tag fb-sla-tag--risk">🟡 At Risk</span>;
  if (['complaint', 'feedback'].includes(item.type)) return <span className="fb-sla-tag fb-sla-tag--ok">🟢 On Track</span>;
  return null;
}

// ═══════════════════════════════════════════════════════════════
// STAR RATING
// ═══════════════════════════════════════════════════════════════

function StarRating({ value, onChange, readonly = false }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="fb-stars" role="group" aria-label="Rating">
      {[1,2,3,4,5].map(star => (
        <button key={star} type="button"
          className={`fb-star${(hover || value) >= star ? ' fb-star--active' : ''}`}
          onClick={() => !readonly && onChange(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
          disabled={readonly} aria-label={`${star} star`}
        >★</button>
      ))}
      {value > 0 && <span className="fb-star-label">{['','Poor','Fair','Good','Great','Excellent'][value]}</span>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// DIMENSIONAL RATINGS (Airbnb-style per-aspect)
// ═══════════════════════════════════════════════════════════════

function StarRow({ label, value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="fb-dim-row">
      <span className="fb-dim-row__label" id={`fb-dim-${label}`}>{label}</span>
      <div className="fb-dim-row__stars" role="group" aria-labelledby={`fb-dim-${label}`}>
        {[1,2,3,4,5].map(s => (
          <button key={s} type="button"
            className={`fb-dim-star${(hover || value) >= s ? ' fb-dim-star--on' : ''}`}
            onClick={() => onChange(s)}
            onMouseEnter={() => setHover(s)}
            onMouseLeave={() => setHover(0)}
            aria-label={`Rate ${label} ${s} star${s === 1 ? '' : 's'}`}
            aria-pressed={value === s}
          >★</button>
        ))}
      </div>
      <span className="fb-dim-row__val">{value > 0 ? `${value}/5` : '—'}</span>
    </div>
  );
}

function DimensionalRatings({ values, onChange }) {
  return (
    <div className="fb-dimensions">
      <p className="fb-dimensions__title">Rate each aspect <span className="fb-form__optional">(optional)</span></p>
      {DIMENSIONS.map(d => (
        <StarRow key={d.key} label={d.label} value={values[d.key] || 0}
          onChange={v => onChange({ ...values, [d.key]: v })} />
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// NPS SCORE 0-10 (Apple / Google style)
// ═══════════════════════════════════════════════════════════════

function NpsScore({ value, onChange }) {
  const getColor = (n) => n <= 6 ? '#ef4444' : n <= 8 ? '#f59e0b' : '#22c55e';
  const getLabel = (n) => n === null ? '' : n <= 6 ? 'Detractor' : n <= 8 ? 'Passive' : 'Promoter 🎉';
  return (
    <div className="fb-nps">
      <div className="fb-nps__header">
        <label className="fb-form__label">
          How likely are you to recommend Desi Bites to a friend? <span className="fb-form__required">*</span>
        </label>
        {value !== null && (
          <span className="fb-nps__badge" style={{ color: getColor(value) }}>
            {value}/10 — {getLabel(value)}
          </span>
        )}
      </div>
      <div className="fb-nps__scale">
        <span className="fb-nps__hint">Not at all</span>
        <div className="fb-nps__buttons">
          {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
            <button key={n} type="button"
              className={`fb-nps-btn${value === n ? ' fb-nps-btn--active' : ''}`}
              style={{ '--nps-color': getColor(n) }}
              onClick={() => onChange(n)}
            >{n}</button>
          ))}
        </div>
        <span className="fb-nps__hint">Extremely likely</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MOOD PICKER (Starbucks / McDonald's survey style)
// ═══════════════════════════════════════════════════════════════

function MoodPicker({ value, onChange }) {
  return (
    <div className="fb-mood-wrap">
      <h3 className="fb-mood-title">How was your experience?</h3>
      <p className="fb-mood-sub">Tap the emoji that best describes your visit</p>
      <div className="fb-mood-row">
        {MOODS.map(m => (
          <button key={m.emoji} type="button"
            className={`fb-mood-btn${value?.emoji === m.emoji ? ' fb-mood-btn--active' : ''}`}
            style={{ '--mood-color': m.color }}
            onClick={() => onChange(m)}
          >
            <span className="fb-mood-emoji">{m.emoji}</span>
            <span className="fb-mood-label">{m.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PROGRESS BAR
// ═══════════════════════════════════════════════════════════════

function ProgressBar({ step }) {
  const steps = ['Experience', 'Details', 'Contact & Submit'];
  return (
    <div className="fb-progress">
      {steps.map((label, i) => (
        <React.Fragment key={label}>
          <div className={`fb-progress__step${i < step - 1 ? ' fb-progress__step--done' : i === step - 1 ? ' fb-progress__step--active' : ''}`}>
            <div className="fb-progress__dot">{i < step - 1 ? '✓' : i + 1}</div>
            <span className="fb-progress__label">{label}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`fb-progress__line${i < step - 1 ? ' fb-progress__line--done' : ''}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MULTI-STEP FEEDBACK FORM
// ═══════════════════════════════════════════════════════════════

function FeedbackForm({ onSubmitted }) {
  const { user } = useAuth();
  const [step,       setStep]       = useState(1);
  const [form,       setForm]       = useState({ ...EMPTY_FORM, name: user?.displayName || '', email: user?.email || '' });
  const [submitting, setSubmitting] = useState(false);
  const [success,    setSuccess]    = useState(false);
  const [refCode,    setRefCode]    = useState('');
  const [error,      setError]      = useState('');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const nextStep1 = () => {
    if (!form.mood) { setError('Please select your experience.'); return; }
    setError(''); setStep(2);
  };

  const nextStep2 = () => {
    if (!form.message.trim()) { setError('Please describe your experience.'); return; }
    setError(''); setStep(3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.npsScore === null) { setError('Please provide your recommendation score.'); return; }
    setError(''); setSubmitting(true);
    try {
      const code = Date.now().toString(36).toUpperCase();
      await addDoc(collection(db, 'feedback'), {
        ...form,
        name:  form.anonymous ? 'Anonymous' : (form.name.trim()  || 'Guest'),
        email: form.anonymous ? '' : form.email.trim(),
        phone: form.anonymous ? '' : form.phone.trim(),
        status:         'pending',
        priority:       form.mood?.type === 'complaint' ? 'high' : form.mood?.type === 'feedback' ? 'medium' : 'low',
        refCode:        code,
        submittedAt:    Timestamp.now(),
        submittedBy:    user?.uid || null,
        acknowledgedAt: null, acknowledgedBy: null,
        resolvedAt:     null, resolvedBy:     null,
        assignedTo:     null, adminNotes:     '', publicReply: '', upvotes: 0,
      });
      setRefCode(code); setSuccess(true); onSubmitted?.();
    } catch (err) {
      console.error(err); setError('Something went wrong. Please try again.');
    } finally { setSubmitting(false); }
  };

  const resetForm = () => {
    setSuccess(false); setStep(1); setRefCode(''); setError('');
    setForm({ ...EMPTY_FORM, name: user?.displayName || '', email: user?.email || '' });
  };

  if (success) {
    return (
      <div className="fb-success">
        <div className="fb-success__confetti">🎉</div>
        <h3>Thank you for your feedback!</h3>
        <p>Your voice matters. Our team reviews every submission and takes real action.</p>
        <div className="fb-success__ref">
          <span className="fb-success__ref-label">Your reference number</span>
          <span className="fb-success__ref-code">#{refCode}</span>
        </div>
        <div className="fb-success__next">
          <p>What happens next?</p>
          <ul>
            <li>✅ Your feedback is in our team's inbox</li>
            <li>🔍 A manager will review within 24 hours</li>
            {!form.anonymous && form.email && <li>📧 We'll email you when it's resolved</li>}
          </ul>
        </div>
        <button className="fb-btn fb-btn--outline" onClick={resetForm}>Submit Another</button>
      </div>
    );
  }

  return (
    <div className="fb-form-wrap">
      <ProgressBar step={step} />

      {/* ─── STEP 1: Mood ─── */}
      {step === 1 && (
        <div className="fb-step">
          <MoodPicker value={form.mood} onChange={m => { set('mood', m); set('type', m.type); }} />
          {form.mood && (
            <div className="fb-type-refine">
              <label className="fb-form__label">Refine type</label>
              <div className="fb-type-grid">
                {FEEDBACK_TYPES.map(t => (
                  <button key={t.value} type="button"
                    className={`fb-type-tile${form.type === t.value ? ' fb-type-tile--active' : ''}`}
                    style={{ '--tile-color': t.color }} onClick={() => set('type', t.value)}
                  >{t.label}</button>
                ))}
              </div>
            </div>
          )}
          {error && <div className="fb-form__error">⚠️ {error}</div>}
          <div className="fb-step-actions fb-step-actions--right">
            <button type="button" className="fb-btn fb-btn--primary" onClick={nextStep1}>Continue →</button>
          </div>
        </div>
      )}

      {/* ─── STEP 2: Ratings + Message ─── */}
      {step === 2 && (
        <form className="fb-step fb-form" onSubmit={e => { e.preventDefault(); nextStep2(); }}>
          <div className="fb-form__group">
            <label className="fb-form__label" htmlFor="fb-cat">Category</label>
            <select id="fb-cat" className="fb-select" value={form.category} onChange={e => set('category', e.target.value)}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <DimensionalRatings values={form.dimensionalRatings} onChange={v => set('dimensionalRatings', v)} />
          <div className="fb-form__group">
            <label className="fb-form__label">Overall Rating</label>
            <StarRating value={form.rating} onChange={v => set('rating', v)} />
          </div>
          <div className="fb-form__group">
            <label className="fb-form__label" htmlFor="fb-subject">Subject <span className="fb-form__optional">(optional)</span></label>
            <input id="fb-subject" className="fb-input" type="text"
              placeholder="Brief headline of your feedback…"
              value={form.subject} onChange={e => set('subject', e.target.value)} maxLength={120} />
          </div>
          <div className="fb-form__group">
            <label className="fb-form__label" htmlFor="fb-msg">Tell us more <span className="fb-form__required">*</span></label>
            <textarea id="fb-msg" className="fb-textarea" rows={5}
              placeholder="Share what happened, when it happened, and what could be improved…"
              value={form.message} onChange={e => set('message', e.target.value)} maxLength={1500} required />
            <span className="fb-char-count">{form.message.length}/1500</span>
          </div>
          <div className="fb-form__row">
            <div className="fb-form__group fb-form__group--half">
              <label className="fb-form__label" htmlFor="fb-date">Visit Date <span className="fb-form__optional">(optional)</span></label>
              <input id="fb-date" className="fb-input" type="date" value={form.visitDate} onChange={e => set('visitDate', e.target.value)} />
            </div>
            <div className="fb-form__group fb-form__group--half">
              <label className="fb-form__label" htmlFor="fb-order">Order / Receipt # <span className="fb-form__optional">(optional)</span></label>
              <input id="fb-order" className="fb-input" type="text" placeholder="e.g. #1042"
                value={form.orderNumber} onChange={e => set('orderNumber', e.target.value)} maxLength={40} />
            </div>
          </div>
          {error && <div className="fb-form__error">⚠️ {error}</div>}
          <div className="fb-step-actions">
            <button type="button" className="fb-btn fb-btn--ghost" onClick={() => { setStep(1); setError(''); }}>← Back</button>
            <button type="submit" className="fb-btn fb-btn--primary">Continue →</button>
          </div>
        </form>
      )}

      {/* ─── STEP 3: NPS + Contact ─── */}
      {step === 3 && (
        <form className="fb-step fb-form" onSubmit={handleSubmit} noValidate>
          <NpsScore value={form.npsScore} onChange={v => set('npsScore', v)} />
          <div className="fb-form__group">
            <label className="fb-toggle">
              <input type="checkbox" checked={form.anonymous} onChange={e => set('anonymous', e.target.checked)} />
              <span className="fb-toggle__slider" />
              <span className="fb-toggle__text">Submit anonymously</span>
            </label>
            <p className="fb-form__hint">Anonymous feedback is still reviewed — we just can't follow up with you.</p>
          </div>
          {!form.anonymous && (
            <div className="fb-contact-section">
              <p className="fb-contact-section__title">📞 Contact info (so we can follow up)</p>
              <div className="fb-form__row">
                <div className="fb-form__group fb-form__group--third">
                  <label className="fb-form__label" htmlFor="fb-name">Name</label>
                  <input id="fb-name" className="fb-input" type="text" placeholder="Your name"
                    value={form.name} onChange={e => set('name', e.target.value)} maxLength={80} />
                </div>
                <div className="fb-form__group fb-form__group--third">
                  <label className="fb-form__label" htmlFor="fb-email">Email</label>
                  <input id="fb-email" className="fb-input" type="email" placeholder="you@example.com"
                    value={form.email} onChange={e => set('email', e.target.value)} maxLength={120} />
                </div>
                <div className="fb-form__group fb-form__group--third">
                  <label className="fb-form__label" htmlFor="fb-phone">Phone <span className="fb-form__optional">(optional)</span></label>
                  <input id="fb-phone" className="fb-input" type="tel" placeholder="(555) 000-0000"
                    value={form.phone} onChange={e => set('phone', e.target.value)} maxLength={20} />
                </div>
              </div>
            </div>
          )}
          {error && <div className="fb-form__error">⚠️ {error}</div>}
          <div className="fb-step-actions">
            <button type="button" className="fb-btn fb-btn--ghost" onClick={() => { setStep(2); setError(''); }}>← Back</button>
            <button type="submit" className="fb-btn fb-btn--primary" disabled={submitting}>
              {submitting ? <><span className="fb-btn__spinner" /> Submitting…</> : '🚀 Send Feedback'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// PUBLIC SUGGESTIONS BOARD (Canny-style upvoting)
// ═══════════════════════════════════════════════════════════════

function PublicSuggestionsBoard({ allItems, onUpvote }) {
  const [sortBy, setSortBy] = useState('votes');

  const suggestions = useMemo(() => {
    const list = allItems.filter(i => i.type === 'suggestion');
    if (sortBy === 'votes')  return [...list].sort((a,b) => (b.upvotes||0) - (a.upvotes||0));
    if (sortBy === 'newest') return [...list].sort((a,b) => (b.submittedAt?.seconds||0) - (a.submittedAt?.seconds||0));
    return list;
  }, [allItems, sortBy]);

  const [votedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('fb_voted') || '[]')); }
    catch { return new Set(); }
  });

  const handleVote = (id) => {
    if (votedIds.has(id)) return;
    votedIds.add(id);
    localStorage.setItem('fb_voted', JSON.stringify([...votedIds]));
    onUpvote(id);
  };

  const statusColor = { pending: '#f59e0b', acknowledged: '#f59e0b', resolved: '#22c55e' };
  const statusLabel = { pending: 'Open', acknowledged: 'In Review', resolved: 'Shipped ✅' };

  return (
    <div className="fb-sugg-board">
      <div className="fb-sugg-board__header">
        <div>
          <h3 className="fb-sugg-board__title">💡 Community Suggestions</h3>
          <p className="fb-sugg-board__sub">Vote for the suggestions you want us to act on. Top-voted ideas get priority.</p>
        </div>
        <select className="fb-select fb-select--sm" value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="votes">Top Voted</option>
          <option value="newest">Newest</option>
        </select>
      </div>
      {suggestions.length === 0 ? (
        <div className="fb-empty">
          <div className="fb-empty__icon">💡</div>
          <p className="fb-empty__msg">No suggestions yet. Be the first to share an idea!</p>
        </div>
      ) : (
        <div className="fb-sugg-list">
          {suggestions.map(s => {
            const voted = votedIds.has(s.id);
            return (
              <div key={s.id} className="fb-sugg-card">
                <button className={`fb-upvote${voted ? ' fb-upvote--voted' : ''}`}
                  onClick={() => handleVote(s.id)} disabled={voted}
                  title={voted ? 'Already voted' : 'Upvote this suggestion'}
                >
                  <span className="fb-upvote__arrow">▲</span>
                  <span className="fb-upvote__count">{s.upvotes || 0}</span>
                </button>
                <div className="fb-sugg-card__body">
                  {s.subject && <h4 className="fb-sugg-card__title">{s.subject}</h4>}
                  <p className="fb-sugg-card__msg">{s.message}</p>
                  <div className="fb-sugg-card__meta">
                    <CategoryLabel category={s.category} />
                    <span className="fb-sugg-status" style={{ '--ss-color': statusColor[s.status] || '#64748b' }}>
                      {statusLabel[s.status] || s.status}
                    </span>
                    {s.publicReply && <span className="fb-sugg-replied">💬 Team replied</span>}
                  </div>
                  {s.publicReply && (
                    <div className="fb-sugg-card__reply">
                      <span className="fb-sugg-card__reply-label">🏮 Desi Bites Team:</span>
                      <p>{s.publicReply}</p>
                    </div>
                  )}
                </div>
                <div className="fb-sugg-card__date">
                  {s.submittedAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ACTIVITY TIMELINE
// ═══════════════════════════════════════════════════════════════

function ActivityTimeline({ item }) {
  const fmt = ts => ts?.toDate?.()?.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) || '—';
  const events = [
    item.submittedAt    && { label: 'Submitted',        ts: item.submittedAt,    icon: '📬', color: '#94a3b8' },
    item.acknowledgedAt && { label: 'Moved to In Review', ts: item.acknowledgedAt, icon: '🔍', color: '#f59e0b' },
    item.resolvedAt     && { label: 'Resolved',          ts: item.resolvedAt,     icon: '✅', color: '#22c55e' },
  ].filter(Boolean);
  return (
    <div className="fb-timeline">
      {events.map((ev, i) => (
        <div key={i} className="fb-timeline-item">
          <div className="fb-timeline-item__dot" style={{ background: ev.color }}>{ev.icon}</div>
          <div className="fb-timeline-item__content">
            <span className="fb-timeline-item__label">{ev.label}</span>
            <span className="fb-timeline-item__ts">{fmt(ev.ts)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FEEDBACK CARD (admin — enhanced)
// ═══════════════════════════════════════════════════════════════

function FeedbackCard({ item, onAcknowledge, onResolve, onUpdateNote, onUpdateReply, onAssign, staffList, selected, onSelectToggle }) {
  const [expanded,    setExpanded]    = useState(false);
  const [note,        setNote]        = useState(item.adminNotes  || '');
  const [reply,       setReply]       = useState(item.publicReply || '');
  const [savingNote,  setSavingNote]  = useState(false);
  const [savingReply, setSavingReply] = useState(false);

  const ts      = item.submittedAt?.toDate?.();
  const dateStr = ts ? ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const timeStr = ts ? ts.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';
  const dimEntries = Object.entries(item.dimensionalRatings || {}).filter(([,v]) => v > 0);

  const handleSaveNote  = async () => { setSavingNote(true);  await onUpdateNote(item.id, note);   setSavingNote(false);  };
  const handleSaveReply = async () => { setSavingReply(true); await onUpdateReply(item.id, reply); setSavingReply(false); };

  return (
    <div className={`fb-card fb-card--${item.type}${selected ? ' fb-card--selected' : ''}`}>
      {/* Header */}
      <div className="fb-card__header">
        <label className="fb-checkbox-wrap" onClick={e => e.stopPropagation()}>
          <input type="checkbox" checked={selected} onChange={() => onSelectToggle(item.id)} />
          <span className="fb-checkbox" />
        </label>
        <div className="fb-card__meta" onClick={() => setExpanded(e => !e)} style={{ cursor: 'pointer', flex: 1 }}>
          <TypeBadge type={item.type} />
          <CategoryLabel category={item.category} />
          <PriorityBadge priority={item.priority} />
          <SlaTag item={item} />
        </div>
        <div className="fb-card__stars-small" onClick={() => setExpanded(e => !e)}>
          {'★'.repeat(item.rating || 0)}{'☆'.repeat(5 - (item.rating || 0))}
        </div>
        {(item.upvotes > 0) && <span className="fb-card__votes">▲ {item.upvotes}</span>}
        <button className="fb-card__expand" onClick={() => setExpanded(e => !e)} aria-label="Expand">
          {expanded ? '▲' : '▼'}
        </button>
      </div>

      {/* Body */}
      <div className="fb-card__body" onClick={() => setExpanded(e => !e)} style={{ cursor: 'pointer' }}>
        {item.subject && <h4 className="fb-card__subject">"{item.subject}"</h4>}
        <p className={`fb-card__message${expanded ? ' fb-card__message--full' : ''}`}>{item.message}</p>
      </div>

      {/* Footer */}
      <div className="fb-card__footer">
        <div className="fb-card__author">
          <span className="fb-card__avatar">{(item.name || 'G')[0].toUpperCase()}</span>
          <div>
            <span className="fb-card__name">{item.anonymous ? '🎭 Anonymous' : item.name || 'Guest'}</span>
            {!item.anonymous && item.email && <span className="fb-card__contact">{item.email}</span>}
            {!item.anonymous && item.phone && <span className="fb-card__contact">{item.phone}</span>}
          </div>
        </div>
        <div className="fb-card__date">
          <span>{dateStr} {timeStr}</span>
          {item.visitDate   && <span>Visit: {item.visitDate}</span>}
          {item.orderNumber && <span>Order: {item.orderNumber}</span>}
          {item.npsScore != null && <span>NPS: {item.npsScore}/10</span>}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="fb-card__detail">
          {/* Dimension bars */}
          {dimEntries.length > 0 && (
            <div className="fb-card__dims">
              <span className="fb-form__label">Aspect Ratings</span>
              <div className="fb-card__dim-bars">
                {dimEntries.map(([key, val]) => {
                  const dim = DIMENSIONS.find(d => d.key === key);
                  return (
                    <div key={key} className="fb-card__dim-bar-row">
                      <span className="fb-card__dim-bar-label">{dim?.label || key}</span>
                      <div className="fb-card__dim-bar-track">
                        <div className="fb-card__dim-bar-fill" style={{ width: `${val * 20}%` }} />
                      </div>
                      <span className="fb-card__dim-bar-val">{val}/5</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div>
            <span className="fb-form__label">Activity Timeline</span>
            <ActivityTimeline item={item} />
          </div>

          {/* Assign */}
          <div className="fb-card__assign">
            <label className="fb-form__label" htmlFor={`assign-${item.id}`}>Assign To</label>
            <select id={`assign-${item.id}`} className="fb-select fb-select--sm"
              value={item.assignedTo || ''}
              onChange={e => onAssign(item.id, e.target.value || null)}
            >
              <option value="">— Unassigned —</option>
              {staffList.map(s => <option key={s.uid} value={s.uid}>{s.name} ({s.role})</option>)}
            </select>
          </div>

          {/* Internal Note */}
          <div className="fb-card__note-area">
            <label className="fb-form__label">🔒 Internal Note <span className="fb-form__optional">(staff only)</span></label>
            <textarea className="fb-textarea fb-textarea--sm" rows={3}
              placeholder="Add notes visible only to managers…" value={note} onChange={e => setNote(e.target.value)} />
            <button className="fb-btn fb-btn--sm fb-btn--outline" onClick={handleSaveNote} disabled={savingNote}>
              {savingNote ? 'Saving…' : 'Save Note'}
            </button>
          </div>

          {/* Public Reply */}
          <div className="fb-card__note-area">
            <label className="fb-form__label">💬 Public Reply <span className="fb-form__optional">(shown on suggestions board)</span></label>
            <textarea className="fb-textarea fb-textarea--sm" rows={3}
              placeholder="Reply that the customer can see…" value={reply} onChange={e => setReply(e.target.value)} />
            <button className="fb-btn fb-btn--sm fb-btn--acknowledge" onClick={handleSaveReply} disabled={savingReply}>
              {savingReply ? 'Saving…' : '📤 Post Reply'}
            </button>
          </div>

          {/* Actions */}
          <div className="fb-card__actions">
            {item.status === 'pending' && (
              <button className="fb-btn fb-btn--sm fb-btn--acknowledge" onClick={() => onAcknowledge(item.id)}>
                🔍 Move to In Review
              </button>
            )}
            {item.status === 'acknowledged' && (
              <button className="fb-btn fb-btn--sm fb-btn--resolve" onClick={() => onResolve(item.id)}>
                ✅ Mark Resolved
              </button>
            )}
            {item.acknowledgedAt && (
              <span className="fb-card__ack-tag">
                In Review since {item.acknowledgedAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
            {item.resolvedAt && (
              <span className="fb-card__res-tag">
                ✅ Resolved {item.resolvedAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// BULK ACTIONS BAR (floating — Intercom-style)
// ═══════════════════════════════════════════════════════════════

function BulkActionsBar({ count, onAcknowledge, onResolve, onClear }) {
  if (count === 0) return null;
  return (
    <div className="fb-bulk-bar">
      <span className="fb-bulk-bar__count">{count} selected</span>
      <button className="fb-btn fb-btn--sm fb-btn--acknowledge" onClick={onAcknowledge}>🔍 Move to In Review</button>
      <button className="fb-btn fb-btn--sm fb-btn--resolve"     onClick={onResolve}>✅ Resolve All</button>
      <button className="fb-btn fb-btn--sm fb-btn--ghost"       onClick={onClear}>✕ Clear</button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// 7-DAY TREND CHART (CSS — no library needed)
// ═══════════════════════════════════════════════════════════════

function TrendChart({ items }) {
  const days = useMemo(() => {
    const out = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const key      = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const dayStart = new Date(d); dayStart.setHours(0,0,0,0);
      const dayEnd   = new Date(d); dayEnd.setHours(23,59,59,999);
      const count    = items.filter(item => { const ts = item.submittedAt?.toDate?.(); return ts && ts >= dayStart && ts <= dayEnd; }).length;
      out.push({ key, count });
    }
    return out;
  }, [items]);
  const max = Math.max(...days.map(d => d.count), 1);
  return (
    <div className="fb-trend">
      <h4 className="fb-trend__title">📈 Last 7 Days</h4>
      <div className="fb-trend__chart">
        {days.map(d => (
          <div key={d.key} className="fb-trend__col">
            <span className="fb-trend__val">{d.count > 0 ? d.count : ''}</span>
            <div className="fb-trend__bar" style={{ height: `${(d.count / max) * 100}%` }} />
            <span className="fb-trend__label">{d.key}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CATEGORY BREAKDOWN (horizontal bars)
// ═══════════════════════════════════════════════════════════════

function CategoryBreakdown({ items }) {
  const counts = useMemo(() => {
    const map = {};
    items.forEach(i => { map[i.category] = (map[i.category] || 0) + 1; });
    return Object.entries(map).sort((a,b) => b[1] - a[1]).slice(0, 6);
  }, [items]);
  const max = Math.max(...counts.map(([,c]) => c), 1);
  return (
    <div className="fb-breakdown">
      <h4 className="fb-breakdown__title">📊 By Category</h4>
      {counts.map(([cat, count]) => {
        const c = CATEGORIES.find(x => x.value === cat);
        return (
          <div key={cat} className="fb-breakdown__row">
            <span className="fb-breakdown__label">{c?.label || cat}</span>
            <div className="fb-breakdown__track">
              <div className="fb-breakdown__fill" style={{ width: `${(count/max)*100}%` }} />
            </div>
            <span className="fb-breakdown__count">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ENHANCED STATS BAR
// ═══════════════════════════════════════════════════════════════

function StatsBar({ items }) {
  const total       = items.length;
  const complaints  = items.filter(i => i.type === 'complaint').length;
  const suggestions = items.filter(i => i.type === 'suggestion').length;
  const compliments = items.filter(i => i.type === 'compliment').length;
  const pending     = items.filter(i => i.status === 'pending').length;
  const resolved    = items.filter(i => i.status === 'resolved').length;
  const avgRating   = total ? (items.reduce((s,i) => s + (i.rating||0), 0) / total).toFixed(1) : '—';

  const npsItems = items.filter(i => i.npsScore != null);
  const nps = npsItems.length ? Math.round(
    ((npsItems.filter(i => i.npsScore >= 9).length - npsItems.filter(i => i.npsScore <= 6).length) / npsItems.length) * 100
  ) : null;

  const actionable   = items.filter(i => ['complaint','feedback'].includes(i.type));
  const responded    = actionable.filter(i => i.acknowledgedAt || i.resolvedAt);
  const responseRate = actionable.length ? Math.round((responded.length / actionable.length) * 100) : null;

  const resolvedWithTime = items.filter(i => i.resolvedAt && i.submittedAt);
  const avgResH = resolvedWithTime.length
    ? Math.round(resolvedWithTime.reduce((sum,i) => sum + (i.resolvedAt.seconds - i.submittedAt.seconds) / 3600, 0) / resolvedWithTime.length)
    : null;

  const stats = [
    { label: 'Total',         value: total,                               icon: '📋', color: '#f59e0b' },
    { label: 'Pending',       value: pending,                             icon: '📬', color: '#f59e0b' },
    { label: 'Complaints',    value: complaints,                          icon: '⚠️', color: '#ef4444' },
    { label: 'Suggestions',   value: suggestions,                         icon: '💡', color: '#38bdf8' },
    { label: 'Compliments',   value: compliments,                         icon: '🌟', color: '#22c55e' },
    { label: 'Avg Rating',    value: avgRating,                           icon: '⭐', color: '#f59e0b' },
    { label: 'NPS Score',     value: nps != null ? nps : '—',            icon: '📊', color: nps >= 50 ? '#22c55e' : nps >= 0 ? '#f59e0b' : '#ef4444' },
    { label: 'Response Rate', value: responseRate != null ? `${responseRate}%` : '—', icon: '⚡', color: '#a78bfa' },
    { label: 'Avg Resolve',   value: avgResH != null ? `${avgResH}h` : '—', icon: '🏁', color: '#34d399' },
    { label: 'Resolved',      value: resolved,                            icon: '✅', color: '#22c55e' },
  ];

  return (
    <div className="fb-stats">
      {stats.map(s => (
        <div key={s.label} className="fb-stat-card">
          <span className="fb-stat-card__icon">{s.icon}</span>
          <span className="fb-stat-card__value" style={{ color: s.color }}>{s.value}</span>
          <span className="fb-stat-card__label">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// EXPORT CSV
// ═══════════════════════════════════════════════════════════════

function exportCsv(items) {
  const headers = ['Date','Type','Category','Rating','NPS','Subject','Message','Name','Email','Phone','Status','Priority','Order#','Visit Date','Admin Notes'];
  const rows = items.map(i => [
    i.submittedAt?.toDate?.()?.toLocaleString() || '',
    i.type, i.category, i.rating || '', i.npsScore ?? '',
    `"${(i.subject||'').replace(/"/g,'""')}"`,
    `"${(i.message||'').replace(/"/g,'""')}"`,
    i.anonymous ? 'Anonymous' : (i.name||''),
    i.anonymous ? '' : (i.email||''),
    i.anonymous ? '' : (i.phone||''),
    i.status, i.priority, i.orderNumber||'', i.visitDate||'',
    `"${(i.adminNotes||'').replace(/"/g,'""')}"`,
  ]);
  const csv  = [headers, ...rows].map(r => r.join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `feedback-${new Date().toISOString().slice(0,10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
}

// ═══════════════════════════════════════════════════════════════
// ADMIN DASHBOARD
// ═══════════════════════════════════════════════════════════════

function FeedbackDashboard({ allItems, onRefresh, loading }) {
  const { user } = useAuth();
  const [activeTab,   setActiveTab]   = useState('pending');
  const [typeFilter,  setTypeFilter]  = useState('all');
  const [catFilter,   setCatFilter]   = useState('all');
  const [sortBy,      setSortBy]      = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [selected,    setSelected]    = useState(new Set());
  const [staffList,   setStaffList]   = useState([]);
  const [items,       setItems]       = useState(allItems);

  useEffect(() => { setItems(allItems); }, [allItems]);

  useEffect(() => {
    getDocs(collection(db, 'users')).then(snap => {
      setStaffList(snap.docs
        .map(d => ({ uid: d.id, ...d.data() }))
        .filter(u => MANAGE_ROLES.includes(u.role))
        .map(u => ({ uid: u.uid, name: u.name || u.email, role: u.role }))
      );
    }).catch(console.error);
  }, []);

  const mutate = (id, patch) => setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));

  const handleAcknowledge  = async (id) => { await updateDoc(doc(db,'feedback',id), { status:'acknowledged', acknowledgedAt:Timestamp.now(), acknowledgedBy:user.uid }); mutate(id, { status:'acknowledged', acknowledgedAt:Timestamp.now() }); };
  const handleResolve      = async (id) => { await updateDoc(doc(db,'feedback',id), { status:'resolved',      resolvedAt:Timestamp.now(),    resolvedBy:user.uid    }); mutate(id, { status:'resolved',      resolvedAt:Timestamp.now()    }); };
  const handleUpdateNote   = async (id, note)  => { await updateDoc(doc(db,'feedback',id), { adminNotes: note  }); mutate(id, { adminNotes: note  }); };
  const handleUpdateReply  = async (id, reply) => { await updateDoc(doc(db,'feedback',id), { publicReply: reply }); mutate(id, { publicReply: reply }); };
  const handleAssign       = async (id, uid)   => { await updateDoc(doc(db,'feedback',id), { assignedTo: uid  }); mutate(id, { assignedTo: uid  }); };
  const handleUpvote       = async (id) => { await updateDoc(doc(db,'feedback',id), { upvotes: increment(1) }); mutate(id, { upvotes: (items.find(i=>i.id===id)?.upvotes||0)+1 }); };

  const handleBulkAcknowledge = async () => { for (const id of selected) { if (items.find(i=>i.id===id)?.status==='pending') await handleAcknowledge(id); } setSelected(new Set()); };
  const handleBulkResolve     = async () => { for (const id of selected) { if (items.find(i=>i.id===id)?.status!=='resolved') await handleResolve(id); } setSelected(new Set()); };
  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const filtered = useMemo(() => {
    let list = items.filter(i => i.status === activeTab);
    if (typeFilter !== 'all') list = list.filter(i => i.type     === typeFilter);
    if (catFilter  !== 'all') list = list.filter(i => i.category === catFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(i => (i.message||'').toLowerCase().includes(q) || (i.subject||'').toLowerCase().includes(q) || (i.name||'').toLowerCase().includes(q));
    }
    const PRIORITY_ORDER = { urgent:0, high:1, medium:2, low:3 };
    if (sortBy === 'oldest')    return [...list].reverse();
    if (sortBy === 'rating-hi') return [...list].sort((a,b) => (b.rating||0)-(a.rating||0));
    if (sortBy === 'rating-lo') return [...list].sort((a,b) => (a.rating||0)-(b.rating||0));
    if (sortBy === 'votes')     return [...list].sort((a,b) => (b.upvotes||0)-(a.upvotes||0));
    if (sortBy === 'priority')  return [...list].sort((a,b) => (PRIORITY_ORDER[a.priority]||3)-(PRIORITY_ORDER[b.priority]||3));
    return list;
  }, [items, activeTab, typeFilter, catFilter, searchQuery, sortBy]);

  const tabCounts = {
    pending:      items.filter(i => i.status==='pending').length,
    acknowledged: items.filter(i => i.status==='acknowledged').length,
    resolved:     items.filter(i => i.status==='resolved').length,
  };

  const overdueCount = items.filter(i => {
    if (i.status !== 'pending' || i.type === 'compliment') return false;
    return (Date.now() - (i.submittedAt?.toDate?.()?.getTime?.() || 0)) / 3600000 > 24;
  }).length;

  return (
    <div className="fb-dashboard">
      <div className="fb-dashboard__header">
        <div>
          <h2 className="fb-dashboard__title">Feedback Dashboard</h2>
          {overdueCount > 0 && (
            <div className="fb-overdue-alert">
              🔴 {overdueCount} complaint{overdueCount > 1 ? 's' : ''} overdue — needs immediate attention
            </div>
          )}
        </div>
        <div className="fb-dashboard__actions">
          <button className="fb-btn fb-btn--sm fb-btn--outline" onClick={() => exportCsv(items)}>⬇️ Export CSV</button>
          <button className="fb-btn fb-btn--sm fb-btn--outline" onClick={onRefresh}>🔄 Refresh</button>
        </div>
      </div>

      <StatsBar items={items} />

      <div className="fb-charts-row">
        <TrendChart items={items} />
        <CategoryBreakdown items={items} />
      </div>

      <div className="fb-tabs">
        {STATUS_TABS.map(tab => (
          <button key={tab.value}
            className={`fb-tab${activeTab === tab.value ? ' fb-tab--active' : ''}`}
            style={activeTab === tab.value ? { '--tab-active-color': tab.color } : {}}
            onClick={() => setActiveTab(tab.value)}
          >
            {tab.icon} {tab.label}
            {tabCounts[tab.value] > 0 && (
              <span className={`fb-tab__badge${tab.value==='pending' && tabCounts.pending>0 ? ' fb-tab__badge--alert' : ''}`}>
                {tabCounts[tab.value]}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="fb-filter-bar">
        <input className="fb-input fb-input--search" type="text"
          placeholder="🔍 Search by name, subject, message…"
          value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        <select className="fb-select fb-select--sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="all">All Types</option>
          {FEEDBACK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select className="fb-select fb-select--sm" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="all">All Categories</option>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select className="fb-select fb-select--sm" value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="priority">By Priority</option>
          <option value="rating-hi">Highest Rated</option>
          <option value="rating-lo">Lowest Rated</option>
          <option value="votes">Most Voted</option>
        </select>
        {filtered.length > 0 && (
          <button className="fb-btn fb-btn--sm fb-btn--ghost"
            onClick={() => { const all = new Set(filtered.map(i=>i.id)); setSelected(prev => prev.size===all.size ? new Set() : all); }}
          >{selected.size === filtered.length ? '☐ Deselect All' : '☑ Select All'}</button>
        )}
      </div>

      {loading ? (
        <div className="fb-loading"><div className="fb-loading__spinner" /><p>Loading feedback…</p></div>
      ) : filtered.length === 0 ? (
        <div className="fb-empty">
          <div className="fb-empty__icon">{activeTab==='pending' ? '📭' : activeTab==='acknowledged' ? '🔍' : '✅'}</div>
          <p className="fb-empty__msg">{searchQuery ? 'No results match your search.' : `No ${activeTab} feedback yet.`}</p>
        </div>
      ) : (
        <div className="fb-list">
          {filtered.map(item => (
            <FeedbackCard key={item.id} item={item}
              onAcknowledge={handleAcknowledge} onResolve={handleResolve}
              onUpdateNote={handleUpdateNote}   onUpdateReply={handleUpdateReply}
              onAssign={handleAssign}           staffList={staffList}
              selected={selected.has(item.id)} onSelectToggle={toggleSelect}
            />
          ))}
        </div>
      )}

      <BulkActionsBar count={selected.size}
        onAcknowledge={handleBulkAcknowledge}
        onResolve={handleBulkResolve}
        onClear={() => setSelected(new Set())}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════

export default function FeedbackPage() {
  const { role } = useAuth();
  const isManager = MANAGE_ROLES.includes(role);

  useSeo({
    title: 'Share Your Feedback',
    description: 'Tell us about your Desi Bites experience — rate your food, service, and more. Your feedback helps us serve Northwest Arkansas better.',
    path: '/feedback',
  });

  const [view,        setView]        = useState(isManager ? 'dashboard' : 'submit');
  const [allItems,    setAllItems]    = useState([]);
  const [publicItems, setPublicItems] = useState([]);
  const [loading,     setLoading]     = useState(isManager);

  const loadAll = useCallback(async () => {
    if (!isManager) return;
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'feedback'), orderBy('submittedAt', 'desc')));
      setAllItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [isManager]);

  useEffect(() => {
    getDocs(query(collection(db, 'feedback'), orderBy('upvotes', 'desc'))).then(snap => {
      setPublicItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }).catch(console.error);
  }, []);

  useEffect(() => { if (isManager) loadAll(); }, [loadAll, isManager]);

  const handleUpvote = async (id) => {
    await updateDoc(doc(db, 'feedback', id), { upvotes: increment(1) });
    setPublicItems(prev => prev.map(i => i.id === id ? { ...i, upvotes: (i.upvotes||0)+1 } : i));
  };

  const VIEWS = isManager
    ? [
        { key: 'dashboard', label: '📊 Dashboard' },
        { key: 'submit',    label: '✍️ Submit Feedback' },
        { key: 'board',     label: '💡 Suggestions Board' },
      ]
    : [
        { key: 'submit', label: '✍️ Give Feedback' },
        { key: 'board',  label: '💡 Suggestions Board' },
      ];

  return (
    <div className="fb-page">
      <div className="fb-hero">
        <div className="fb-hero__content">
          <div className="fb-hero__badge">Feedback Center</div>
          <h1 className="fb-hero__title">We listen. We improve.</h1>
          <p className="fb-hero__sub">
            Every review, complaint, and suggestion shapes how we serve you.
            Your voice directly influences our kitchen, our team, and your experience.
          </p>
          <div className="fb-view-toggle">
            {VIEWS.map(v => (
              <button key={v.key}
                className={`fb-view-btn${view === v.key ? ' fb-view-btn--active' : ''}`}
                onClick={() => setView(v.key)}
              >{v.label}</button>
            ))}
          </div>
        </div>
        <div className="fb-hero__deco" aria-hidden>
          <div className="fb-deco-ring fb-deco-ring--1"><span>⭐</span></div>
          <div className="fb-deco-ring fb-deco-ring--2"><span>💬</span></div>
          <div className="fb-deco-ring fb-deco-ring--3"><span>💡</span></div>
        </div>
      </div>

      <div className="fb-body">
        {view === 'dashboard' && isManager && (
          <FeedbackDashboard allItems={allItems} onRefresh={loadAll} loading={loading} />
        )}
        {view === 'submit' && (
          <div className="fb-split">
            <div className="fb-split__form">
              <div className="fb-section-title">
                <span className="fb-section-title__icon">✍️</span>
                <div>
                  <h2>Share your experience</h2>
                  <p>Takes less than 2 minutes. All feedback is reviewed by leadership.</p>
                </div>
              </div>
              <FeedbackForm onSubmitted={() => { if (isManager) loadAll(); }} />
            </div>
            <div className="fb-split__sidebar">
              <div className="fb-trust-card"><div className="fb-trust-card__icon">🔒</div><h4>Your privacy is safe</h4><p>All submissions are encrypted. Anonymous option available.</p></div>
              <div className="fb-trust-card"><div className="fb-trust-card__icon">⚡</div><h4>Fast response</h4><p>Complaints are reviewed within 24 hours by our management team.</p></div>
              <div className="fb-trust-card"><div className="fb-trust-card__icon">🎯</div><h4>Real action taken</h4><p>Every suggestion is tracked — from submission to resolution.</p></div>
              <div className="fb-trust-card"><div className="fb-trust-card__icon">💌</div><h4>We follow up</h4><p>Leave your email and we'll personally reach out with an update.</p></div>
              <div className="fb-category-legend">
                <h4 className="fb-category-legend__title">What can you report?</h4>
                {CATEGORIES.map(c => <div key={c.value} className="fb-category-legend__item"><span>{c.label}</span></div>)}
              </div>
            </div>
          </div>
        )}
        {view === 'board' && (
          <PublicSuggestionsBoard allItems={publicItems} onUpvote={handleUpvote} />
        )}
      </div>
    </div>
  );
}
